import { baseLocale, isLocale, type Locale } from '@/paraglide/runtime.js'

type WeightedTag = {
    tag: string
    quality: number
}

const parseWeightedTags = (acceptLanguage: string): WeightedTag[] =>
    acceptLanguage
        .split(',')
        .map((part, index) => {
            const [tag, ...params] = part.trim().split(';')
            const qualityParam = params.map((param) => param.trim()).find((param) => param.startsWith('q='))
            const parsedQuality = qualityParam ? Number.parseFloat(qualityParam.slice(2)) : 1

            return {
                tag: (tag ?? '').trim().toLowerCase(),
                // Preserve source order for equal weights: Accept-Language is an
                // ordered preference list, and Array.prototype.sort is only stable
                // if we make the tiebreak explicit.
                quality: Number.isNaN(parsedQuality) ? 0 : parsedQuality - index * Number.EPSILON,
            }
        })
        .filter(({ tag, quality }) => tag.length > 0 && quality > 0)
        .sort((a, b) => b.quality - a.quality)

/**
 * The visitor's most-preferred locale that this app actually supports, or
 * undefined when their preferences give us nothing to offer.
 *
 * Only safe to call while rendering a response that is never shared-cached.
 * Accept-Language is high-cardinality, so branching on it inside cacheable HTML
 * would fragment the cache. See docs/solutions/locale-cache-contract.md.
 */
export const preferredSupportedLocale = (acceptLanguage: string | null | undefined): Locale | undefined => {
    if (!acceptLanguage) {
        return undefined
    }

    for (const { tag } of parseWeightedTags(acceptLanguage)) {
        if (tag === '*') {
            return undefined
        }

        // `es-419` and `es-MX` are both offers of Spanish; we only ship one.
        const primarySubtag = tag.split('-')[0]

        if (isLocale(primarySubtag)) {
            return primarySubtag
        }
    }

    return undefined
}

/**
 * The two languages to offer, or undefined when there is nothing to offer.
 *
 * Always a pair: the base locale, and the one alternative that is relevant to
 * this visitor. Listing every shipped locale would grow the control every time
 * we add a language and would ask people to choose between options most of
 * them cannot read. A pair stays the same size at five locales or fifty.
 *
 * The alternative is whatever they are reading right now, falling back to what
 * their browser asked for. Preferring the current locale matters: someone
 * reading Spanish must keep seeing Spanish in the control, or switching to
 * English would be a one-way door.
 */
export const localeSwitcherOptions = ({
    acceptLanguage,
    currentLocale,
}: {
    acceptLanguage: string | null | undefined
    currentLocale: Locale
}): [Locale, Locale] | undefined => {
    const alternative = currentLocale !== baseLocale ? currentLocale : preferredSupportedLocale(acceptLanguage)

    if (!alternative || alternative === baseLocale) {
        return undefined
    }

    return [baseLocale, alternative]
}
